import type { Schema } from '@/api/client'
import { formatDayMonth } from '@/lib/format'

type SearchDashboard = Schema<'SearchDashboardOut'>
type KeywordSeries = Schema<'KeywordSeriesOut'>

/**
 * The arithmetic behind `RankChart.tsx`: the window's buckets, each keyword's
 * average per bucket, and the path that draws it. Its own module so the chart
 * file exports components alone (react-refresh) and so the figures the table
 * prints are the figures the line was drawn from.
 */

/** Whether the response carries anything for the keyword chart to draw. */
export function hasKeywordLines(data: SearchDashboard): boolean {
  return (data.keywords ?? []).some((line) => line.points.length > 0)
}

export interface Buckets {
  /** The first day of each bucket, ISO. */
  starts: string[]
  labels: string[]
  length: number
  unit: 'day' | 'week'
}

export interface Line {
  name: string
  values: (number | null)[]
}

const DAY_MS = 86_400_000

export function bucketsFor(data: SearchDashboard): Buckets {
  const start = new Date(`${data.start_date}T00:00:00Z`)
  const end = new Date(`${data.end_date}T00:00:00Z`)
  const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1)
  const unit = days <= 7 ? 'day' : 'week'
  const size = unit === 'day' ? 1 : 7
  const count = Math.ceil(days / size)
  const starts = Array.from({ length: count }, (_, i) =>
    new Date(start.getTime() + i * size * DAY_MS).toISOString().slice(0, 10),
  )
  const labels =
    unit === 'day'
      ? starts.map((on) => formatDayMonth(`${on}T00:00:00Z`, 'UTC'))
      : starts.map((_, i) => `W${String(i + 1)}`)
  return { starts, labels, length: count, unit }
}

export function seriesFor(keywords: KeywordSeries[], buckets: Buckets): Line[] {
  const size = buckets.unit === 'day' ? 1 : 7
  const first = new Date(`${buckets.starts[0] ?? '1970-01-01'}T00:00:00Z`).getTime()
  return keywords
    .filter((line) => line.points.length > 0)
    .map((line) => {
      const sums = new Array<number>(buckets.length).fill(0)
      const counts = new Array<number>(buckets.length).fill(0)
      for (const point of line.points) {
        const at = new Date(`${point.on}T00:00:00Z`).getTime()
        const index = Math.floor((at - first) / (size * DAY_MS))
        if (index < 0 || index >= buckets.length) continue
        sums[index] = (sums[index] ?? 0) + point.position
        counts[index] = (counts[index] ?? 0) + 1
      }
      return {
        name: line.keyword,
        values: sums.map((sum, i) => {
          const n = counts[i] ?? 0
          return n === 0 ? null : Math.round((sum / n) * 10) / 10
        }),
      }
    })
}

export function isNumber(value: number | null): value is number {
  return value !== null
}

export function lastValue(values: (number | null)[]): { index: number; value: number } | null {
  for (let i = values.length - 1; i >= 0; i -= 1) {
    const value = values[i]
    if (value !== null && value !== undefined) return { index: i, value }
  }
  return null
}

/** A path that lifts the pen over a bucket with no reading. */
export function pathOf(
  values: (number | null)[],
  x: (index: number) => number,
  y: (rank: number) => number,
): string {
  let drawing = false
  const parts: string[] = []
  values.forEach((value, index) => {
    if (value === null) {
      drawing = false
      return
    }
    parts.push(`${drawing ? 'L' : 'M'}${String(x(index))} ${String(y(value))}`)
    drawing = true
  })
  return parts.join(' ')
}
